import React, { useEffect, useRef } from 'react'

/**
 * THE SIGNATURE ENGINE — realism pass
 * Each vibe is a living subject in a real world: a horizon, directional
 * light, a breathing subject with imperfect motion, and atmosphere.
 * Pure canvas. No assets. Micro-randomness — no two are ever identical.
 */

type Sig = {
  init: (w: number, h: number, R: () => number) => any
  draw: (ctx: CanvasRenderingContext2D, s: any, p: number, w: number, h: number, c: string, k: number) => void
}

const TAU = Math.PI * 2
const ease = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: string, a: number) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, c)
  g.addColorStop(1, 'transparent')
  ctx.globalAlpha = a
  ctx.fillStyle = g
  ctx.fillRect(x - r, y - r, r * 2, r * 2)
  ctx.globalAlpha = 1
}

// a soft ground/water plane with gradient depth
function plane(ctx: CanvasRenderingContext2D, w: number, h: number, horizon: number, c: string, alpha: number) {
  const g = ctx.createLinearGradient(0, horizon, 0, h)
  g.addColorStop(0, c)
  g.addColorStop(1, 'transparent')
  ctx.globalAlpha = alpha
  ctx.fillStyle = g
  ctx.fillRect(0, horizon, w, h - horizon)
  ctx.globalAlpha = 1
}

// atmospheric particles (mist, snow, pollen, dust)
function motes(ctx: CanvasRenderingContext2D, arr: any[], w: number, h: number, p: number, c: string, size = 2) {
  for (const m of arr) {
    const y = ((m.y + p * m.v) % 1) * h
    const x = m.x * w + Math.sin(y / 38 + m.sway) * 9
    glow(ctx, x, y, size, c, m.o * 0.5)
  }
}

const SIGNATURES: Record<string, Sig> = {

  // ── LOVE — a lotus on a still dawn pond, mist on the water ───────────────
  LOVE: {
    init: (w, h, R) => ({
      petals: Array.from({ length: 13 }, (_, i) => ({
        ring: i < 8 ? 0 : 1, idx: i < 8 ? i : i - 8, n: i < 8 ? 8 : 5, wob: (R() - 0.5) * 0.12,
      })),
      mist: Array.from({ length: 22 }, () => ({ x: R(), y: 0.6 + R() * 0.35, v: 0.04 + R() * 0.06, o: 0.12 + R() * 0.2, sway: R() * TAU })),
      dragonfly: { seed: R() * TAU },
      ripples: [] as number[],
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h * 0.6, waterY = h * 0.66
      // dawn sky glow
      const sky = ctx.createLinearGradient(0, 0, 0, waterY)
      sky.addColorStop(0, 'rgba(20,10,25,0)')
      sky.addColorStop(1, `${c}14`)
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, waterY)
      // still water plane with a horizon line of light
      plane(ctx, w, h, waterY, c, 0.10)
      ctx.globalAlpha = 0.3
      glow(ctx, cx, waterY, w * 0.5, c, 0.12)
      ctx.globalAlpha = 1
      // reflection of the bloom, shimmering on water
      const open = ease(clamp((p - 0.2) / 0.4))
      // lily pad beneath
      ctx.globalAlpha = 0.5 * clamp(p / 0.2)
      ctx.fillStyle = '#0d2a1e'
      ctx.beginPath(); ctx.ellipse(cx, cy + 30, 78, 24, 0, 0, TAU); ctx.fill()
      ctx.globalAlpha = 1
      // petals unfold with directional dawn light
      const petal = (ang: number, len: number, wid: number, alpha: number) => {
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(ang)
        ctx.globalAlpha = alpha
        const g = ctx.createLinearGradient(0, 0, Math.sin(0.5) * wid, -len)
        g.addColorStop(0, c); g.addColorStop(0.6, '#FF9EC4'); g.addColorStop(1, '#FFE1EC')
        ctx.fillStyle = g
        ctx.beginPath(); ctx.moveTo(0, 0)
        ctx.quadraticCurveTo(wid, -len * 0.45, 0, -len)
        ctx.quadraticCurveTo(-wid, -len * 0.45, 0, 0)
        ctx.fill()
        // subtle vein highlight (light from upper-left)
        ctx.globalAlpha = alpha * 0.4; ctx.strokeStyle = '#FFF0F6'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(0, -len * 0.15); ctx.lineTo(-wid * 0.15, -len * 0.85); ctx.stroke()
        ctx.restore(); ctx.globalAlpha = 1
      }
      for (const pt of s.petals) {
        const start = pt.ring === 0 ? 0.05 : 0.25
        const t = ease(clamp((p - start) / 0.5)); if (t <= 0) continue
        const spread = pt.ring === 0 ? 1.05 : 0.55
        const base = ((pt.idx / pt.n) - 0.5) * 2
        const breath = Math.sin(p * TAU * 0.8 + pt.idx) * 0.02 * t
        petal(base * spread * t + pt.wob * t + breath, (pt.ring === 0 ? 122 : 84) * (0.35 + 0.65 * t), pt.ring === 0 ? 29 : 22, 0.6 + 0.28 * t)
      }
      // glowing golden heart — beats scale with intensity
      const b1 = clamp((p - 0.52) / 0.09), b2 = clamp((p - 0.68) / 0.09)
      const b3 = k > 1.2 ? clamp((p - 0.82) / 0.09) : 0  // charged gets a third beat
      const beat = (Math.sin(b1 * Math.PI) * 0.7 + Math.sin(b2 * Math.PI) + Math.sin(b3 * Math.PI)) * k
      glow(ctx, cx, cy - 30, 16 + open * 12 + beat * 24, '#FFEBA0', 0.9 * open)
      glow(ctx, cx, cy - 30, (52 + beat * 44) * (0.85 + k * 0.15), c, 0.28 * open * k)
      // mist drifting over water
      for (const m of s.mist) {
        const mx = ((m.x + p * m.v) % 1) * w
        glow(ctx, mx, m.y * h + Math.sin(p * 2 + m.sway) * 4, 26, '#ffffff', m.o * 0.4 * clamp(p * 2))
      }
      // a dragonfly visits at the end
      if (p > 0.62) {
        const dt = (p - 0.62) / 0.38
        const dx = cx + Math.cos(s.dragonfly.seed + dt * 5) * (140 - dt * 60)
        const dy = cy - 70 + Math.sin(s.dragonfly.seed + dt * 7) * 40
        glow(ctx, dx, dy, 4, '#9fe8ff', 0.7 * (1 - dt * 0.3))
        ctx.globalAlpha = 0.5 * (1 - dt * 0.3); ctx.strokeStyle = '#cdefff'; ctx.lineWidth = 1
        const flutter = Math.sin(p * 80) * 5
        ctx.beginPath(); ctx.moveTo(dx - 7, dy - flutter); ctx.lineTo(dx + 7, dy + flutter); ctx.stroke()
        ctx.globalAlpha = 1
      }
    },
  },

  // ── HELD — an otter floats on dusk water cradling its pup ────────────────
  HELD: {
    init: (w, h, R) => ({
      stars: Array.from({ length: 18 }, () => ({ x: R(), y: R() * 0.38, o: 0.1 + R() * 0.25, tw: R() * TAU })),
      seed: R() * TAU,
      ripples: Array.from({ length: 4 }, (_, i) => i * 0.25),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h * 0.56, waterY = h * 0.5
      const t = ease(clamp(p / 0.4))
      const bob = Math.sin(p * TAU * 1.4 + s.seed) * 5 * t
      // dusk sky gradient
      const sky = ctx.createLinearGradient(0, 0, 0, waterY)
      sky.addColorStop(0, '#1a0f22'); sky.addColorStop(1, `${c}22`)
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, waterY)
      for (const st of s.stars) glow(ctx, st.x * w, st.y * h, 1.8, '#FFE9C9', st.o * (0.6 + 0.4 * Math.sin(st.tw + p * 4)) * t)
      // water
      plane(ctx, w, h, waterY, c, 0.14)
      // horizon light band (dusk sun sinking)
      glow(ctx, cx, waterY, w * 0.55, c, 0.2 * t)
      // ripples from the floating pair
      for (const r0 of s.ripples) {
        const rt = ((p * 0.7 + r0) % 1)
        ctx.globalAlpha = (1 - rt) * 0.22 * t; ctx.strokeStyle = c; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.ellipse(cx, cy + 40 + bob * 0.4, 60 + rt * 165, 13 + rt * 32, 0, 0, TAU); ctx.stroke()
      }
      ctx.globalAlpha = 1
      glow(ctx, cx, cy + bob, 115, c, 0.3 * t)
      // otter on its back, silhouette rimmed by dusk light
      ctx.save(); ctx.translate(cx, cy + bob); ctx.globalAlpha = 0.94 * t
      ctx.fillStyle = '#241610'
      ctx.beginPath(); ctx.ellipse(0, 8, 82, 33, 0, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(-76, -12, 23, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(-89, -29, 6, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(-63, -30, 6, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.ellipse(-92, -6, 9, 6, 0, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.moveTo(70, 14); ctx.quadraticCurveTo(124, 10, 136, -14); ctx.quadraticCurveTo(118, 22, 72, 26); ctx.fill()
      // pup nuzzles
      const nuzzle = Math.sin(clamp((p - 0.55) / 0.3) * Math.PI) * 0.14
      ctx.save(); ctx.translate(-4, -22); ctx.rotate(-0.08 + nuzzle)
      ctx.fillStyle = '#38271a'
      ctx.beginPath(); ctx.ellipse(0, 0, 33, 15, 0, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(-29, -6, 11, 0, TAU); ctx.fill()
      ctx.fillStyle = '#1a1008'
      ctx.beginPath(); ctx.arc(-35, -14, 3, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(-23, -14, 3, 0, TAU); ctx.fill()
      ctx.restore()
      // warm rim light along the top edge (light from the sinking sun)
      ctx.globalAlpha = 0.55 * t; ctx.strokeStyle = c; ctx.lineWidth = 2.5
      ctx.beginPath(); ctx.ellipse(0, 6, 84, 35, 0, Math.PI * 1.02, Math.PI * 1.98); ctx.stroke()
      ctx.restore(); ctx.globalAlpha = 1
    },
  },

  // ── I SEE YOU — a firefly finds you across a dark meadow ─────────────────
  I_SEE_YOU: {
    init: (w, h, R) => ({
      grass: Array.from({ length: 60 }, () => ({ x: R(), hgt: 20 + R() * 45, sway: R() * TAU, bend: 0.1 + R() * 0.15 })),
      others: Array.from({ length: 12 }, () => ({ x: R(), y: 0.3 + R() * 0.5, ph: R() * TAU, sp: 0.5 + R() * 0.5 })),
      path: { seed: R() * TAU },
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const groundY = h * 0.72
      // night meadow
      plane(ctx, w, h, groundY, '#0a1a0f', 0.5)
      // distant fireflies blinking
      for (const f of s.others) {
        const blink = Math.max(0, Math.sin(p * 6 * f.sp + f.ph))
        glow(ctx, f.x * w, f.y * h, 3, c, blink * 0.4)
      }
      // grass silhouettes swaying
      ctx.strokeStyle = '#06120a'; ctx.lineWidth = 2.5
      for (const g of s.grass) {
        const sway = Math.sin(p * 2 + g.sway) * g.bend
        ctx.globalAlpha = 0.8
        ctx.beginPath(); ctx.moveTo(g.x * w, h)
        ctx.quadraticCurveTo(g.x * w + sway * 30, h - g.hgt * 0.6, g.x * w + sway * 60, h - g.hgt)
        ctx.stroke()
      }
      ctx.globalAlpha = 1
      // THE firefly — wanders, then comes to you (center) and holds, pulsing
      const cx = w / 2, cy = h * 0.46
      const find = ease(clamp(p / 0.6))
      const wanderX = Math.cos(s.path.seed + (1 - find) * 8) * w * 0.4 * (1 - find)
      const wanderY = Math.sin(s.path.seed + (1 - find) * 6) * h * 0.2 * (1 - find)
      const fx = cx + wanderX, fy = cy + wanderY
      // its glow pulses like breathing — brighter as it locks on
      const pulse = 0.6 + 0.4 * Math.sin(p * 10)
      const lock = clamp((p - 0.6) / 0.2)
      glow(ctx, fx, fy, 8 + lock * 6, '#e8ffd0', (0.7 + lock * 0.3) * pulse)
      glow(ctx, fx, fy, 30 + lock * 40, c, (0.3 + lock * 0.3) * pulse)
      // when found — a soft ring, being seen
      if (lock > 0) {
        ctx.globalAlpha = lock * (1 - clamp((p - 0.85) / 0.15)) * 0.6
        ctx.strokeStyle = c; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.arc(fx, fy, 40 + (1 - lock) * 40, 0, TAU); ctx.stroke()
        ctx.globalAlpha = 1
      }
    },
  },

  // ── PROUD — dawn breaks over a mountain ridge, just for you ──────────────
  PROUD: {
    init: (w, h, R) => ({
      birds: Array.from({ length: 5 }, (_, i) => ({ off: i * 0.5, y: 0.2 + R() * 0.15, sp: 0.8 + R() * 0.4 })),
      rays: Array.from({ length: 11 }, (_, i) => i),
      motes: Array.from({ length: 30 }, () => ({ x: R(), y: R(), v: 0.15 + R() * 0.3, o: 0.2 + R() * 0.4, sway: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const ridgeY = h * 0.66
      const t = ease(clamp(p / 0.7))
      const sunY = ridgeY - t * h * 0.36
      const sunX = w / 2
      // sky warming from night to dawn
      const sky = ctx.createLinearGradient(0, 0, 0, ridgeY)
      sky.addColorStop(0, `rgba(30,20,40,${1 - t * 0.4})`)
      sky.addColorStop(0.7, `${c}${Math.floor(t * 40).toString(16).padStart(2, '0')}`)
      sky.addColorStop(1, c + '66')
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, ridgeY)
      // god rays from the rising sun
      for (const i of s.rays) {
        const a = -Math.PI / 2 + (i - 5) * 0.17
        ctx.save(); ctx.translate(sunX, sunY); ctx.rotate(a)
        const g = ctx.createLinearGradient(0, 0, 0, -h)
        g.addColorStop(0, c); g.addColorStop(1, 'transparent')
        ctx.globalAlpha = 0.09 * t; ctx.fillStyle = g
        ctx.fillRect(-9, 0, 18, -h); ctx.restore()
      }
      ctx.globalAlpha = 1
      // the sun
      glow(ctx, sunX, sunY, 60 + t * 30, '#FFF4D0', 0.9 * t)
      glow(ctx, sunX, sunY, 160, c, 0.3 * t)
      // dust motes rising in the light
      for (const m of s.motes) {
        const my = h - ((m.y + p * m.v) % 1) * h
        glow(ctx, m.x * w, my, 2, '#FFE9B0', m.o * t * 0.5)
      }
      // mountain ridge silhouette
      ctx.fillStyle = '#0a0812'
      ctx.beginPath(); ctx.moveTo(0, ridgeY)
      ctx.lineTo(w * 0.2, ridgeY - 30); ctx.lineTo(w * 0.38, ridgeY - 8)
      ctx.lineTo(w * 0.55, ridgeY - 44); ctx.lineTo(w * 0.72, ridgeY - 14)
      ctx.lineTo(w * 0.88, ridgeY - 34); ctx.lineTo(w, ridgeY - 6)
      ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); ctx.fill()
      // birds crossing the dawn
      if (p > 0.3) {
        ctx.strokeStyle = '#0a0812'; ctx.lineWidth = 2.5; ctx.globalAlpha = 0.7
        for (const b of s.birds) {
          const bp = clamp((p - 0.3 - b.off * 0.1) * b.sp)
          const bx = bp * w * 1.2 - w * 0.1, by = b.y * h + Math.sin(bp * 8) * 8
          const flap = Math.sin(p * 20 + b.off) * 6
          ctx.beginPath(); ctx.moveTo(bx - 9, by + flap); ctx.lineTo(bx, by); ctx.lineTo(bx + 9, by + flap); ctx.stroke()
        }
        ctx.globalAlpha = 1
      }
    },
  },

  // ── SPARK — real lightning forks over a night field ─────────────────────
  SPARK: {
    init: (w, h, R) => {
      const bolt = () => {
        const path: any[] = []; let x = w * (0.35 + R() * 0.3), y = -10
        while (y < h * 0.7) { path.push({ x, y }); y += 12 + R() * 20; x += (R() - 0.5) * 54 }
        return path
      }
      const branches = [0.35, 0.6].map((at) => ({ at, path: bolt() }))
      return { main: bolt(), branches, flashSeed: R() }
    },
    draw: (ctx, s, p, w, h, c, k) => {
      const groundY = h * 0.78
      // stormy field
      plane(ctx, w, h, groundY, '#0a0a14', 0.6)
      const drawBolt = (pts: any[], frac: number, lw: number, a: number) => {
        const n = Math.max(2, Math.floor(pts.length * frac))
        ctx.globalAlpha = a; ctx.strokeStyle = '#f0f8ff'; ctx.lineWidth = lw
        ctx.shadowColor = c; ctx.shadowBlur = 18
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y)
        for (let i = 1; i < n; i++) ctx.lineTo(pts[i].x, pts[i].y)
        ctx.stroke(); ctx.shadowBlur = 0; ctx.globalAlpha = 1
      }
      const grow = clamp(p / 0.28)
      // the whole sky flashes when the bolt completes
      if (p > 0.28 && p < 0.44) {
        const f = 1 - (p - 0.28) / 0.16
        ctx.globalAlpha = f * 0.5 * k; ctx.fillStyle = c; ctx.fillRect(0, 0, w, groundY)
        ctx.globalAlpha = f * Math.min(0.95, 0.85 * k); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h * 0.3)
        ctx.globalAlpha = 1
      }
      drawBolt(s.main, grow, 3, 0.95)
      if (grow > 0.6) for (const b of s.branches) drawBolt(b.path, (grow - 0.6) * 2.5, 1.8, 0.7)
      // ground lights up, silhouette of a lone tree
      if (p > 0.28) {
        const gl = 1 - clamp((p - 0.28) / 0.5)
        glow(ctx, w / 2, groundY, w * 0.6, c, 0.3 * gl)
      }
      ctx.fillStyle = '#050508'
      ctx.beginPath(); ctx.moveTo(w * 0.15, h); ctx.lineTo(w * 0.15, groundY - 40)
      ctx.lineTo(w * 0.1, groundY - 55); ctx.lineTo(w * 0.15, groundY - 48)
      ctx.lineTo(w * 0.2, groundY - 62); ctx.lineTo(w * 0.15, groundY - 45)
      ctx.lineTo(w * 0.15, h); ctx.stroke()
      // residual glow
      if (p >= 0.44) drawBolt(s.main, 1, 2, 0.4 * (1 - clamp((p - 0.44) / 0.5)))
    },
  },

  // ── HYPE — fireworks bloom over a city skyline ──────────────────────────
  HYPE: {
    init: (w, h, R) => ({
      sparks: Array.from({ length: 80 }, () => ({ a: R() * TAU, v: 0.4 + R() * 1, s: 1.5 + R() * 3, o: 0.5 + R() * 0.5, hue: R() })),
      trails: Array.from({ length: 3 }, (_, i) => ({ x: 0.3 + i * 0.2, delay: i * 0.08 })),
      skyline: Array.from({ length: 14 }, () => ({ w0: 0.04 + R() * 0.05, hgt: 0.05 + R() * 0.14 })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const groundY = h * 0.82
      // night city
      plane(ctx, w, h, groundY, '#0e0a1a', 0.5)
      // skyline silhouette with lit windows
      let sx = 0
      ctx.fillStyle = '#08060f'
      for (const b of s.skyline) {
        const bw = b.w0 * w, bh = b.hgt * h
        ctx.fillRect(sx, groundY - bh, bw, bh)
        sx += bw
      }
      // window lights
      for (let i = 0; i < 30; i++) {
        glow(ctx, (i * 137 % w), groundY - (i * 53 % (h * 0.15)) - 4, 1.5, '#ffd98a', 0.4)
      }
      const cx = w / 2, cy = h * 0.36
      if (p < 0.2) {
        // launch trail rising
        const t = p / 0.2
        glow(ctx, cx, groundY - t * (groundY - cy), 4, '#ffd98a', 0.9)
        for (let i = 1; i <= 4; i++) glow(ctx, cx, groundY - (t - i * 0.05) * (groundY - cy), 2, c, 0.3)
      } else {
        // BURST
        const t = ease(clamp((p - 0.2) / 0.8))
        for (const sp of s.sparks) {
          const grav = t * t * 40
          const d = t * sp.v * Math.max(w, h) * 0.42 * (0.8 + k * 0.25)
          const x = cx + Math.cos(sp.a) * d
          const y = cy + Math.sin(sp.a) * d + grav
          const col = sp.hue < 0.4 ? c : sp.hue < 0.7 ? '#ffd98a' : '#ff6bd6'
          glow(ctx, x, y, sp.s * (1 - t * 0.4), col, sp.o * (1 - t))
        }
        glow(ctx, cx, cy, 70 * (1 - t * 0.6), '#ffffff', 0.7 * (1 - t))
        // reflection shimmer on the city
        ctx.globalAlpha = 0.15 * (1 - t); ctx.fillStyle = c; ctx.fillRect(0, 0, w, groundY); ctx.globalAlpha = 1
      }
    },
  },

  // ── LOCKED IN — a hawk fixes on its target, everything stills ───────────
  LOCKED_IN: {
    init: (w, h, R) => ({
      clouds: Array.from({ length: 8 }, () => ({ x: R(), y: 0.15 + R() * 0.4, v: 0.02 + R() * 0.03, s: 40 + R() * 60, o: 0.06 + R() * 0.1 })),
      lines: Array.from({ length: 40 }, () => ({ a: R() * TAU, o: 0.2 + R() * 0.4, v: 0.7 + R() * 0.6 })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h * 0.44
      // high-altitude sky
      const sky = ctx.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0, '#0a1420'); sky.addColorStop(1, `${c}18`)
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h)
      // clouds far below, drifting
      for (const cl of s.clouds) {
        const clx = ((cl.x + p * cl.v) % 1) * w
        glow(ctx, clx, cl.y * h, cl.s, '#ffffff', cl.o)
      }
      const t = ease(clamp(p / 0.7))
      // wind/focus lines converging
      const R0 = Math.max(w, h) * 0.75
      for (const l of s.lines) {
        const outer = R0 * (1 - t * 0.4 * l.v), inner = outer - (55 + t * 90)
        ctx.globalAlpha = l.o * t; ctx.strokeStyle = c; ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.moveTo(cx + Math.cos(l.a) * outer, cy + Math.sin(l.a) * outer)
        ctx.lineTo(cx + Math.cos(l.a) * Math.max(inner, 55), cy + Math.sin(l.a) * Math.max(inner, 55))
        ctx.stroke()
      }
      ctx.globalAlpha = 1
      // the hawk — wings tucking as it locks, diving
      const dive = ease(clamp((p - 0.5) / 0.5))
      ctx.save(); ctx.translate(cx, cy + dive * 30); ctx.globalAlpha = 0.95
      ctx.fillStyle = '#1a1208'
      const wingTuck = 1 - t * 0.7 // wings fold in as focus sharpens
      // body
      ctx.beginPath(); ctx.ellipse(0, 0, 8, 26 - dive * 6, 0, 0, TAU); ctx.fill()
      // wings
      ctx.beginPath()
      ctx.moveTo(0, -8)
      ctx.quadraticCurveTo(-60 * wingTuck, -20 * wingTuck, -70 * wingTuck, 10 * wingTuck)
      ctx.quadraticCurveTo(-30 * wingTuck, 0, 0, 6); ctx.fill()
      ctx.beginPath()
      ctx.moveTo(0, -8)
      ctx.quadraticCurveTo(60 * wingTuck, -20 * wingTuck, 70 * wingTuck, 10 * wingTuck)
      ctx.quadraticCurveTo(30 * wingTuck, 0, 0, 6); ctx.fill()
      // head + focused eye
      ctx.beginPath(); ctx.arc(0, -24, 6, 0, TAU); ctx.fill()
      glow(ctx, 0, -25, 3, c, t)
      ctx.restore(); ctx.globalAlpha = 1
      // target ring contracting to a point
      const ring = 130 * (1 - t) + 30
      ctx.globalAlpha = 0.85 * t; ctx.strokeStyle = c; ctx.lineWidth = 2
      ctx.beginPath(); ctx.arc(cx, cy + dive * 30, ring, 0, TAU); ctx.stroke()
      ctx.globalAlpha = 1
    },
  },

  // ── AURA — a bird bursts through clouds into radiant open sky ────────────
  AURA: {
    init: (w, h, R) => ({
      clouds: Array.from({ length: 14 }, () => ({ x: R(), y: 0.35 + R() * 0.3, s: 50 + R() * 90, o: 0.15 + R() * 0.25, v: (R() - 0.5) * 0.05 })),
      dust: Array.from({ length: 34 }, () => ({ a: R() * TAU, r: 50 + R() * 130, v: 0.4 + R() * 0.7, o: 0.3 + R() * 0.4 })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h * 0.44
      const t = ease(clamp(p / 0.75))
      // radiant sky opening up
      const sky = ctx.createRadialGradient(cx, cy, 0, cx, cy, h * 0.8)
      sky.addColorStop(0, `${c}55`); sky.addColorStop(1, 'rgba(8,8,12,0.9)')
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h)
      // radiant halos expanding
      for (let i = 0; i < 4; i++) {
        const rt = clamp(t * 1.3 - i * 0.16); if (rt <= 0) continue
        ctx.globalAlpha = (1 - rt) * 0.4; ctx.strokeStyle = c; ctx.lineWidth = 3 - i * 0.5
        ctx.beginPath(); ctx.arc(cx, cy, 50 + rt * 200, 0, TAU); ctx.stroke()
      }
      ctx.globalAlpha = 1
      // clouds parting (drift apart as bird breaks through)
      for (const cl of s.clouds) {
        const part = t * (cl.x < 0.5 ? -1 : 1) * 90
        glow(ctx, cl.x * w + part + Math.sin(p + cl.y) * 10, cl.y * h, cl.s * (1 - t * 0.3), '#ffffff', cl.o * (1 - t * 0.6))
      }
      // the bird soaring up through the gap, wings wide
      const rise = ease(clamp(p / 0.8))
      const by = h * 0.7 - rise * h * 0.32
      ctx.save(); ctx.translate(cx, by); ctx.globalAlpha = 0.92
      ctx.fillStyle = '#14100a'
      const flap = Math.sin(p * 12) * 0.3 + 0.6 // wings mostly wide, soaring
      ctx.beginPath(); ctx.ellipse(0, 0, 6, 20, 0, 0, TAU); ctx.fill()
      // wings spread wide
      ctx.beginPath(); ctx.moveTo(0, -6)
      ctx.quadraticCurveTo(-50, -30 * flap, -78, -8 * flap)
      ctx.quadraticCurveTo(-40, -4, 0, 4); ctx.fill()
      ctx.beginPath(); ctx.moveTo(0, -6)
      ctx.quadraticCurveTo(50, -30 * flap, 78, -8 * flap)
      ctx.quadraticCurveTo(40, -4, 0, 4); ctx.fill()
      ctx.beginPath(); ctx.arc(0, -20, 5, 0, TAU); ctx.fill()
      // golden rim light on wings
      ctx.globalAlpha = 0.6 * t; ctx.strokeStyle = c; ctx.lineWidth = 1.6
      ctx.beginPath(); ctx.moveTo(0, -6); ctx.quadraticCurveTo(-50, -30 * flap, -78, -8 * flap); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(0, -6); ctx.quadraticCurveTo(50, -30 * flap, 78, -8 * flap); ctx.stroke()
      ctx.restore(); ctx.globalAlpha = 1
      // light dust swirling in the radiance
      for (const d of s.dust) {
        const a = d.a + p * d.v * 3
        glow(ctx, cx + Math.cos(a) * d.r, cy + Math.sin(a) * d.r * 0.7, 2.5, c, d.o * t)
      }
      glow(ctx, cx, by, 40, '#fff6d8', 0.5 * t)
    },
  },

  // ── MISS YOU — one leaf lets go of a moonlit branch, and drifts ─────────
  MISS_YOU: {
    init: (w, h, R) => ({
      seed: R() * TAU,
      others: Array.from({ length: 5 }, (_, i) => ({ a: -0.5 + i * 0.28, len: 15 + R() * 8, tr: R() * TAU })),
      stars: Array.from({ length: 20 }, () => ({ x: R(), y: R() * 0.5, o: 0.08 + R() * 0.2, tw: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      // moonlit night
      const sky = ctx.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0, '#0c0e1a'); sky.addColorStop(1, `${c}12`)
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h)
      for (const st of s.stars) glow(ctx, st.x * w, st.y * h, 1.6, '#cdd6ff', st.o * (0.6 + 0.4 * Math.sin(st.tw + p * 3)))
      // the moon, upper left
      glow(ctx, w * 0.22, h * 0.2, 46, '#e8ecff', 0.5)
      glow(ctx, w * 0.22, h * 0.2, 20, '#ffffff', 0.7)
      const leaf = (x: number, y: number, ang: number, size: number, alpha: number) => {
        ctx.save(); ctx.translate(x, y); ctx.rotate(ang); ctx.globalAlpha = alpha
        const g = ctx.createLinearGradient(0, -size, 0, size)
        g.addColorStop(0, '#E4DCFF'); g.addColorStop(1, c)
        ctx.fillStyle = g
        ctx.beginPath(); ctx.moveTo(0, -size)
        ctx.quadraticCurveTo(size * 0.62, 0, 0, size)
        ctx.quadraticCurveTo(-size * 0.62, 0, 0, -size); ctx.fill()
        ctx.strokeStyle = '#ffffff'; ctx.globalAlpha = alpha * 0.4; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(0, -size * 0.8); ctx.lineTo(0, size * 0.8); ctx.stroke()
        ctx.restore(); ctx.globalAlpha = 1
      }
      // branch, moonlit, upper right
      ctx.strokeStyle = c; ctx.globalAlpha = 0.6; ctx.lineWidth = 4
      ctx.beginPath(); ctx.moveTo(w + 10, h * 0.1); ctx.quadraticCurveTo(w * 0.82, h * 0.16, w * 0.55, h * 0.22); ctx.stroke()
      ctx.globalAlpha = 1
      // remaining leaves tremble in the breeze
      for (const o of s.others) {
        const tx = w * (0.6 + o.a * 0.1) + Math.sin(p * 8 + o.tr) * 2
        const ty = h * 0.2 + Math.cos(p * 7 + o.tr) * 1.5
        leaf(tx, ty, o.a + Math.sin(p * 9 + o.tr) * 0.07, o.len, 0.5)
      }
      // THE leaf releases, tumbles, sways down, lands
      const startX = w * 0.58, startY = h * 0.22
      const fall = ease(clamp((p - 0.15) / 0.62))
      const landed = p > 0.77
      const swayX = Math.sin(fall * TAU * 1.4 + s.seed) * w * 0.1
      const x = startX - fall * w * 0.22 + swayX
      const y = startY + fall * (h * 0.6)
      const ang = fall * TAU * 1.1 + Math.sin(fall * 8) * 0.35
      const landX = startX - w * 0.22 + Math.sin(TAU * 1.4 + s.seed) * w * 0.1
      const landY = startY + h * 0.6
      if (p > 0.12) {
        leaf(landed ? landX : x, landed ? landY : y, landed ? 1.4 : ang, 19, 0.92)
        if (!landed) for (let i = 1; i <= 4; i++) {
          const ft = clamp(fall - i * 0.07); if (ft <= 0) continue
          glow(ctx, startX - ft * w * 0.22 + Math.sin(ft * TAU * 1.4 + s.seed) * w * 0.1, startY + ft * (h * 0.6), 3, c, 0.14 * (1 - i / 5))
        }
      }
      // landing ripple on the ground
      if (landed) {
        const rt = clamp((p - 0.77) / 0.23)
        ctx.globalAlpha = (1 - rt) * 0.45; ctx.strokeStyle = c; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.ellipse(landX, landY + 6, 12 + rt * 55, 4 + rt * 15, 0, 0, TAU); ctx.stroke()
        ctx.globalAlpha = 1
      }
    },
  },

  // ── 3AM — a penguin walks alone under the aurora ────────────────────────
  THREE_AM: {
    init: (w, h, R) => ({
      stars: Array.from({ length: 44 }, () => ({ x: R(), y: R() * 0.6, o: 0.08 + R() * 0.3, tw: R() * TAU })),
      snow: Array.from({ length: 30 }, () => ({ x: R(), y: R(), v: 0.15 + R() * 0.3, sway: R() * TAU })),
      auroraSeed: R() * TAU,
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const horizon = h * 0.72
      for (const st of s.stars) {
        const o = st.o * (0.6 + 0.4 * Math.sin(st.tw + p * 5))
        glow(ctx, st.x * w, st.y * h, 1.8, '#AFC5FF', o)
      }
      for (let band = 0; band < 2; band++) {
        ctx.globalAlpha = 0.16 + band * 0.06
        const col = band === 0 ? c : '#00FFD1'
        const g = ctx.createLinearGradient(0, h * 0.08, 0, h * 0.5)
        g.addColorStop(0, col); g.addColorStop(1, 'transparent')
        ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(0, h * (0.14 + band * 0.07))
        for (let x = 0; x <= w; x += 14) {
          const y = h * (0.14 + band * 0.07) + Math.sin(x / 68 + p * 2.2 + s.auroraSeed + band * 2) * 22
          ctx.lineTo(x, y)
        }
        ctx.lineTo(w, h * 0.52); ctx.lineTo(0, h * 0.52); ctx.closePath(); ctx.fill()
      }
      ctx.globalAlpha = 1
      for (const sn of s.snow) {
        const y = ((sn.y + p * sn.v) % 1) * h
        glow(ctx, sn.x * w + Math.sin(y / 40 + sn.sway) * 8, y, 1.6, '#DDE7FF', 0.35)
      }
      ctx.globalAlpha = 0.14; ctx.fillStyle = '#8FB0FF'; ctx.fillRect(0, horizon, w, h - horizon); ctx.globalAlpha = 1
      const px = w * (0.16 + p * 0.6)
      for (let fx = w * 0.16; fx < px - 26; fx += 24) glow(ctx, fx, horizon + 8, 2, '#AFC5FF', 0.25)
      const waddle = Math.sin(p * TAU * 4) * 0.11
      const hop = Math.abs(Math.sin(p * TAU * 4)) * 3
      ctx.save(); ctx.translate(px, horizon - hop); ctx.rotate(waddle)
      ctx.fillStyle = '#0B0F1E'; ctx.beginPath(); ctx.ellipse(0, -22, 15, 22, 0, 0, TAU); ctx.fill()
      ctx.fillStyle = '#E8EEFF'; ctx.beginPath(); ctx.ellipse(2, -18, 9, 15, 0, 0, TAU); ctx.fill()
      ctx.fillStyle = '#0B0F1E'; ctx.beginPath(); ctx.arc(0, -46, 10, 0, TAU); ctx.fill()
      ctx.fillStyle = '#FFB347'; ctx.beginPath(); ctx.moveTo(8, -46); ctx.lineTo(16, -44); ctx.lineTo(8, -42); ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#0B0F1E'; ctx.save(); ctx.rotate(-waddle * 2)
      ctx.beginPath(); ctx.ellipse(-13, -22, 4.5, 13, -0.25, 0, TAU); ctx.fill(); ctx.restore()
      ctx.strokeStyle = '#AFC5FF'; ctx.globalAlpha = 0.5; ctx.lineWidth = 1.4
      ctx.beginPath(); ctx.ellipse(0, -22, 16, 23, 0, Math.PI * 1.15, Math.PI * 1.85); ctx.stroke()
      ctx.restore(); ctx.globalAlpha = 1
      const swell = Math.sin(clamp((p - 0.15) / 0.7) * Math.PI)
      glow(ctx, px, horizon - 30, 60 + swell * 50, c, 0.12 + swell * 0.2)
    },
  },

  // ── DEAD — a firefly's light goes out... then bursts back, a whole swarm ─
  DEAD: {
    init: (w, h, R) => ({
      swarm: Array.from({ length: 50 }, () => ({ a: R() * TAU, v: 0.4 + R() * 1, s: 1.5 + R() * 3, o: 0.5 + R() * 0.5 })),
      grass: Array.from({ length: 40 }, () => ({ x: R(), hgt: 20 + R() * 40, sway: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h / 2, groundY = h * 0.78
      plane(ctx, w, h, groundY, '#0a1410', 0.5)
      // grass
      ctx.strokeStyle = '#06100a'; ctx.lineWidth = 2
      for (const g of s.grass) {
        ctx.globalAlpha = 0.7
        const sway = Math.sin(p * 2 + g.sway) * 0.12
        ctx.beginPath(); ctx.moveTo(g.x * w, h); ctx.quadraticCurveTo(g.x * w + sway * 30, h - g.hgt * 0.6, g.x * w + sway * 50, h - g.hgt); ctx.stroke()
      }
      ctx.globalAlpha = 1
      if (p < 0.24) {
        // one firefly, flickering, dimming to nothing
        const t = p / 0.24
        const flick = Math.max(0, Math.sin(p * 40)) * (1 - t)
        glow(ctx, cx, cy, 10, '#e8ffd0', (0.9 * (1 - t)) + flick * 0.3)
        glow(ctx, cx, cy, 30, c, 0.4 * (1 - t))
      } else if (p < 0.5) {
        // darkness. one faint ember refuses to die.
        const ember = 0.15 + 0.15 * Math.sin(p * 30)
        glow(ctx, cx, cy, 6, c, ember)
      } else {
        // RESURRECTION — the whole meadow erupts in fireflies
        const t = ease(clamp((p - 0.5) / 0.5))
        for (const f of s.swarm) {
          const d = t * f.v * Math.max(w, h) * 0.5 * (0.85 + k * 0.2)
          const x = cx + Math.cos(f.a) * d, y = cy + Math.sin(f.a) * d
          const blink = 0.5 + 0.5 * Math.sin(p * 20 + f.a * 5)
          glow(ctx, x, y, f.s + 1, '#e8ffd0', f.o * (1 - t * 0.5) * blink)
        }
        glow(ctx, cx, cy, 50 * (1 - t * 0.5), c, 0.6 * (1 - t))
      }
    },
  },

  // ── CHAOS — a murmuration of starlings twists wildly, then settles ──────
  CHAOS: {
    init: (w, h, R) => ({
      birds: Array.from({ length: 120 }, () => ({
        bx: R(), by: R(), ph: R() * TAU, sp: 0.6 + R() * 0.8, sz: 1 + R() * 1.5,
      })),
      cluster: { seed: R() * TAU },
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      // dusk sky
      const sky = ctx.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0, `${c}22`); sky.addColorStop(1, '#1a1220')
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h)
      const cx = w / 2, cy = h * 0.44
      if (p < 0.72) {
        // wild swirling murmuration
        const chaos = Math.sin((p / 0.72) * Math.PI) // peaks mid, settles at ends
        for (const b of s.birds) {
          // each bird swirls around a moving attractor with noise
          const swirl = p * b.sp * 8 + b.ph
          const turbX = Math.sin(swirl) * (60 + chaos * 120) + Math.sin(swirl * 2.3) * chaos * 60
          const turbY = Math.cos(swirl * 1.4) * (50 + chaos * 90) + Math.cos(swirl * 3.1) * chaos * 40
          const x = cx + (b.bx - 0.5) * w * 0.5 + turbX
          const y = cy + (b.by - 0.5) * h * 0.35 + turbY
          ctx.globalAlpha = 0.7
          ctx.fillStyle = '#0a0810'
          // little bird chevron
          ctx.save(); ctx.translate(x, y); ctx.rotate(swirl)
          ctx.beginPath(); ctx.moveTo(-b.sz * 2, 0); ctx.lineTo(0, -b.sz); ctx.lineTo(b.sz * 2, 0); ctx.lineTo(0, b.sz * 0.5); ctx.closePath(); ctx.fill()
          ctx.restore()
        }
        ctx.globalAlpha = 1
      } else {
        // ...they settle onto a wire. stillness.
        const t = ease((p - 0.72) / 0.28)
        const wireY = h * 0.5
        ctx.strokeStyle = '#0a0810'; ctx.lineWidth = 1.5; ctx.globalAlpha = t
        ctx.beginPath(); ctx.moveTo(0, wireY); ctx.lineTo(w, wireY); ctx.stroke()
        ctx.globalAlpha = 1
        s.birds.forEach((b: any, i: number) => {
          if (i % 3 !== 0) return
          const targetX = (i / s.birds.length) * w
          const x = lerp(cx + (b.bx - 0.5) * w * 0.5, targetX, t)
          const y = lerp(cy + (b.by - 0.5) * h * 0.35, wireY - 3, t)
          ctx.globalAlpha = 0.8; ctx.fillStyle = '#0a0810'
          ctx.beginPath(); ctx.ellipse(x, y, 2, 3, 0, 0, TAU); ctx.fill()
        })
        ctx.globalAlpha = 1
        glow(ctx, cx, wireY, 40 + t * 20, c, 0.15 * t)
      }
    },
  },


  // ── BIRTHDAY — a candlelit cake in the dark; a wish; confetti bursts ─────
  BIRTHDAY: {
    init: (w, h, R) => ({
      candles: Array.from({ length: 5 }, (_, i) => ({ x: (i - 2) * 22, flick: R() * TAU })),
      confetti: Array.from({ length: 70 }, () => ({
        a: R() * TAU, v: 0.5 + R() * 1.1, spin: R() * TAU, sz: 3 + R() * 4,
        hue: R(), drift: (R() - 0.5) * 60,
      })),
      balloons: Array.from({ length: 6 }, (_, i) => ({ x: 0.12 + i * 0.15 + (R() - 0.5) * 0.06, off: R(), sway: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cakeY = h * 0.62
      const lit = p < 0.42
      // warm candle-glow room
      const glow0 = lit ? 1 : Math.max(0, 1 - (p - 0.42) / 0.08)
      glow(ctx, cx, cakeY - 40, 190, '#FFB347', 0.28 * glow0)
      // the cake — two tiers, silhouette warmed by candlelight
      ctx.fillStyle = '#2a1a22'
      ctx.beginPath(); ctx.ellipse(cx, cakeY + 26, 95, 20, 0, 0, TAU); ctx.fill()
      ctx.fillRect(cx - 95, cakeY - 4, 190, 30)
      ctx.beginPath(); ctx.ellipse(cx, cakeY - 4, 95, 20, 0, 0, TAU); ctx.fill()
      ctx.fillStyle = '#3a2230'
      ctx.fillRect(cx - 62, cakeY - 34, 124, 30)
      ctx.beginPath(); ctx.ellipse(cx, cakeY - 34, 62, 14, 0, 0, TAU); ctx.fill()
      // icing rim light
      ctx.strokeStyle = c; ctx.globalAlpha = 0.5; ctx.lineWidth = 2
      ctx.beginPath(); ctx.ellipse(cx, cakeY - 34, 62, 14, 0, Math.PI, TAU); ctx.stroke()
      ctx.globalAlpha = 1
      // candles + flames (flicker alive, blow out at 0.42)
      for (const cd of s.candles) {
        ctx.fillStyle = '#E8DFF5'
        ctx.fillRect(cx + cd.x - 2, cakeY - 58, 4, 24)
        if (lit) {
          const fl = 0.7 + 0.3 * Math.sin(p * 30 + cd.flick)
          glow(ctx, cx + cd.x, cakeY - 64, 7 + fl * 3, '#FFE9A0', 0.95)
          glow(ctx, cx + cd.x, cakeY - 64, 20, '#FFB347', 0.4 * fl)
        } else if (p < 0.55) {
          // wisps of smoke after the wish
          const st = (p - 0.42) / 0.13
          glow(ctx, cx + cd.x + Math.sin(st * 8) * 4, cakeY - 68 - st * 22, 4, '#aab', 0.3 * (1 - st))
        }
      }
      // THE WISH → confetti eruption + balloons rise
      if (p >= 0.42) {
        const t = ease(clamp((p - 0.42) / 0.58))
        for (const cf of s.confetti) {
          const d = t * cf.v * Math.max(w, h) * 0.5 * (0.85 + k * 0.2)
          const x = cx + Math.cos(cf.a) * d + cf.drift * t
          const y = cakeY - 60 + Math.sin(cf.a) * d * 0.8 + t * t * 90
          const col = cf.hue < 0.33 ? c : cf.hue < 0.66 ? '#FFD34D' : '#00FFD1'
          ctx.save(); ctx.translate(x, y); ctx.rotate(cf.spin + t * 9)
          ctx.globalAlpha = (1 - t * 0.65)
          ctx.fillStyle = col; ctx.fillRect(-cf.sz / 2, -cf.sz / 4, cf.sz, cf.sz / 2)
          ctx.restore()
        }
        ctx.globalAlpha = 1
        // balloons rising past the frame edges
        for (const b of s.balloons) {
          const by = h + 60 - t * (h + 160) * (0.7 + b.off * 0.5)
          const bx = b.x * w + Math.sin(t * 5 + b.sway) * 14
          glow(ctx, bx, by, 16, c, 0.5)
          ctx.globalAlpha = 0.85; ctx.fillStyle = b.off < 0.5 ? c : '#FFD34D'
          ctx.beginPath(); ctx.ellipse(bx, by, 13, 17, 0, 0, TAU); ctx.fill()
          ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1
          ctx.beginPath(); ctx.moveTo(bx, by + 17); ctx.quadraticCurveTo(bx + 4, by + 34, bx - 2, by + 48); ctx.stroke()
          ctx.globalAlpha = 1
        }
        glow(ctx, cx, cakeY - 60, 90 * (1 - t * 0.5), '#fff', 0.5 * (1 - t))
      }
    },
  },

  // ── THANK YOU — a flower bouquet blooms toward you in golden hour light ──
  THANK_YOU: {
    init: (w, h, R) => ({
      stems: Array.from({ length: 5 }, (_, i) => ({
        a: (i - 2) * 0.3, len: 0.8 + R() * 0.25, wob: (R() - 0.5) * 0.1,
        petals: 5 + Math.floor(R() * 2), hue: R(),
      })),
      petalsFall: Array.from({ length: 16 }, () => ({ x: R(), y: R(), v: 0.1 + R() * 0.18, sway: R() * TAU, o: 0.25 + R() * 0.3 })),
      motes: Array.from({ length: 24 }, () => ({ x: R(), y: R(), v: 0.1 + R() * 0.2, o: 0.15 + R() * 0.3, sway: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, baseY = h * 0.86
      // golden hour wash from upper left
      const sun = ctx.createLinearGradient(0, 0, w * 0.7, h)
      sun.addColorStop(0, `${c}30`); sun.addColorStop(1, 'transparent')
      ctx.fillStyle = sun; ctx.fillRect(0, 0, w, h)
      glow(ctx, w * 0.2, h * 0.14, 90, '#FFE9B0', 0.35)
      // dust motes floating in the light
      for (const m of s.motes) {
        const my = h - ((m.y + p * m.v) % 1) * h
        glow(ctx, m.x * w + Math.sin(my / 50 + m.sway) * 8, my, 2, '#FFE9B0', m.o * 0.5)
      }
      // stems grow up, then flowers bloom facing you
      for (const st of s.stems) {
        const grow = ease(clamp((p - 0.05) / 0.4))
        const tipX = cx + Math.sin(st.a) * 90 * grow
        const tipY = baseY - st.len * h * 0.5 * grow
        ctx.strokeStyle = '#1d4a2a'; ctx.lineWidth = 3.5; ctx.globalAlpha = 0.9
        ctx.beginPath(); ctx.moveTo(cx, baseY)
        ctx.quadraticCurveTo(cx + Math.sin(st.a) * 30, baseY - st.len * h * 0.28 * grow, tipX, tipY)
        ctx.stroke(); ctx.globalAlpha = 1
        // bloom
        const bloom = ease(clamp((p - 0.35) / 0.35))
        if (bloom > 0) {
          const petN = st.petals
          const breathe = 1 + Math.sin(p * TAU * 1.2 + st.a * 5) * 0.03
          for (let i = 0; i < petN; i++) {
            const pa = (i / petN) * TAU + st.wob
            const pl = (16 + st.hue * 6) * bloom * breathe * (0.9 + k * 0.1)
            ctx.save(); ctx.translate(tipX, tipY); ctx.rotate(pa)
            const col = st.hue < 0.4 ? c : st.hue < 0.75 ? '#FFB347' : '#FF8DA1'
            const g = ctx.createLinearGradient(0, 0, 0, -pl)
            g.addColorStop(0, col); g.addColorStop(1, '#FFF2E0')
            ctx.fillStyle = g; ctx.globalAlpha = 0.9
            ctx.beginPath(); ctx.moveTo(0, 0)
            ctx.quadraticCurveTo(pl * 0.45, -pl * 0.5, 0, -pl)
            ctx.quadraticCurveTo(-pl * 0.45, -pl * 0.5, 0, 0); ctx.fill()
            ctx.restore(); ctx.globalAlpha = 1
          }
          glow(ctx, tipX, tipY, 6 * bloom, '#FFE9A0', 0.9 * bloom)
        }
      }
      // loose petals drifting down through the light
      if (p > 0.5) {
        for (const pf of s.petalsFall) {
          const py = ((pf.y + (p - 0.5) * pf.v * 2) % 1) * h
          const px = pf.x * w + Math.sin(py / 42 + pf.sway) * 16
          ctx.save(); ctx.translate(px, py); ctx.rotate(py / 30)
          ctx.globalAlpha = pf.o; ctx.fillStyle = c
          ctx.beginPath(); ctx.ellipse(0, 0, 5, 2.5, 0, 0, TAU); ctx.fill()
          ctx.restore(); ctx.globalAlpha = 1
        }
      }
    },
  },

  // ── CONGRATS — golden confetti cannons + a star rises over the crowd ─────
  CONGRATS: {
    init: (w, h, R) => ({
      burstL: Array.from({ length: 45 }, () => ({ a: -0.3 - R() * 1.0, v: 0.5 + R(), sz: 3 + R() * 4, spin: R() * TAU, hue: R() })),
      burstR: Array.from({ length: 45 }, () => ({ a: Math.PI + 0.3 + R() * 1.0, v: 0.5 + R(), sz: 3 + R() * 4, spin: R() * TAU, hue: R() })),
      crowd: Array.from({ length: 26 }, (_, i) => ({ x: i / 26 + (R() - 0.5) * 0.02, hgt: 0.05 + R() * 0.05, jump: R() * TAU })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const groundY = h * 0.84
      // stage-light sky
      const sky = ctx.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0, '#140a20'); sky.addColorStop(1, `${c}18`)
      ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h)
      // the star of the moment — a golden star rising, spinning slow
      const rise = ease(clamp(p / 0.55))
      const sx = w / 2, sy = h * 0.68 - rise * h * 0.34
      ctx.save(); ctx.translate(sx, sy); ctx.rotate(p * 1.4)
      ctx.fillStyle = '#FFD34D'
      ctx.beginPath()
      for (let i = 0; i < 5; i++) {
        const oa = (i / 5) * TAU - Math.PI / 2, ia = oa + TAU / 10
        const R1 = 26 * (0.6 + rise * 0.4), R2 = R1 * 0.42
        if (i === 0) ctx.moveTo(Math.cos(oa) * R1, Math.sin(oa) * R1)
        else ctx.lineTo(Math.cos(oa) * R1, Math.sin(oa) * R1)
        ctx.lineTo(Math.cos(ia) * R2, Math.sin(ia) * R2)
      }
      ctx.closePath(); ctx.fill(); ctx.restore()
      glow(ctx, sx, sy, 50 + rise * 30, '#FFD34D', 0.5 * rise)
      glow(ctx, sx, sy, 120, c, 0.22 * rise)
      // confetti cannons fire from both bottom corners at 0.3
      if (p > 0.3) {
        const t = ease(clamp((p - 0.3) / 0.7))
        const fire = (arr: any[], ox: number) => {
          for (const cf of arr) {
            const d = t * cf.v * Math.max(w, h) * 0.7 * (0.85 + k * 0.2)
            const x = ox + Math.cos(cf.a) * d
            const y = groundY + Math.sin(cf.a) * d + t * t * 130
            const col = cf.hue < 0.4 ? '#FFD34D' : cf.hue < 0.7 ? c : '#fff'
            ctx.save(); ctx.translate(x, y); ctx.rotate(cf.spin + t * 11)
            ctx.globalAlpha = 1 - t * 0.6
            ctx.fillStyle = col; ctx.fillRect(-cf.sz / 2, -cf.sz / 4, cf.sz, cf.sz / 2)
            ctx.restore()
          }
          ctx.globalAlpha = 1
        }
        fire(s.burstL, w * 0.04); fire(s.burstR, w * 0.96)
      }
      // silhouette crowd jumping at the bottom
      ctx.fillStyle = '#0a0714'
      ctx.fillRect(0, groundY, w, h - groundY)
      for (const cr of s.crowd) {
        const jump = p > 0.3 ? Math.abs(Math.sin(p * 9 + cr.jump)) * 9 : 0
        ctx.beginPath(); ctx.arc(cr.x * w, groundY - cr.hgt * h - jump, 6, 0, TAU); ctx.fill()
        ctx.fillRect(cr.x * w - 5, groundY - cr.hgt * h - jump + 4, 10, cr.hgt * h + jump)
      }
    },
  },

  // ── ANNIVERSARY — two lanterns rise over night water and meet ────────────
  ANNIVERSARY: {
    init: (w, h, R) => ({
      stars: Array.from({ length: 26 }, () => ({ x: R(), y: R() * 0.5, o: 0.1 + R() * 0.25, tw: R() * TAU })),
      others: Array.from({ length: 7 }, () => ({ x: R(), off: R() * 0.7, sp: 0.4 + R() * 0.4, sway: R() * TAU })),
      seed: R() * TAU,
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const waterY = h * 0.74
      // night sky + stars
      for (const st of s.stars) glow(ctx, st.x * w, st.y * h, 1.7, '#cdd6ff', st.o * (0.6 + 0.4 * Math.sin(st.tw + p * 3)))
      // still dark water
      plane(ctx, w, h, waterY, '#0a1424', 0.7)
      glow(ctx, w / 2, waterY, w * 0.5, c, 0.1)
      const lantern = (x: number, y: number, size: number, alpha: number) => {
        // paper body, warm inner flame, gentle glow
        glow(ctx, x, y, size * 2.6, '#FFB347', 0.35 * alpha)
        ctx.globalAlpha = 0.92 * alpha
        const g = ctx.createLinearGradient(x, y - size, x, y + size)
        g.addColorStop(0, '#FFE0B0'); g.addColorStop(0.6, '#FF9E5E'); g.addColorStop(1, '#C4552D')
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.moveTo(x - size * 0.62, y - size * 0.5)
        ctx.quadraticCurveTo(x, y - size * 1.15, x + size * 0.62, y - size * 0.5)
        ctx.lineTo(x + size * 0.5, y + size * 0.62)
        ctx.quadraticCurveTo(x, y + size * 0.85, x - size * 0.5, y + size * 0.62)
        ctx.closePath(); ctx.fill()
        glow(ctx, x, y + size * 0.2, size * 0.45, '#FFF3C0', 0.95 * alpha)
        ctx.globalAlpha = 1
        // reflection on the water
        const ry = waterY + (waterY - y) * 0.35
        glow(ctx, x, ry, size * 1.6, '#FF9E5E', 0.13 * alpha)
      }
      // distant lanterns already drifting up
      for (const o of s.others) {
        const ot = clamp(p * o.sp + o.off) % 1
        const oy = waterY - ot * h * 0.6
        lantern(o.x * w, oy, 8, 0.4 * (1 - ot * 0.5))
      }
      // THE two — rise from opposite sides, drift toward each other, meet
      const t = ease(clamp(p / 0.8))
      const meet = ease(clamp((p - 0.55) / 0.35))
      const y1 = waterY + 30 - t * h * 0.5
      const x1 = w * 0.3 + meet * (w * 0.2 - w * 0.045) + Math.sin(p * 4 + s.seed) * 5
      const x2 = w * 0.7 - meet * (w * 0.2 - w * 0.045) + Math.cos(p * 4.4 + s.seed) * 5
      lantern(x1, y1, 17 * (0.9 + k * 0.1), 1)
      lantern(x2, y1 + 8 - meet * 8, 17 * (0.9 + k * 0.1), 1)
      // when they meet — a shared halo, one light
      if (meet > 0.6) {
        const halo = (meet - 0.6) / 0.4
        glow(ctx, (x1 + x2) / 2, y1, 60 * halo, c, 0.5 * halo)
        ctx.globalAlpha = halo * 0.7; ctx.strokeStyle = c; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.arc((x1 + x2) / 2, y1, 34 + (1 - halo) * 30, 0, TAU); ctx.stroke()
        ctx.globalAlpha = 1
      }
    },
  },

  // ── KNOCK — two gentle raps on a door of light ───────────────────────────
  KNOCK: {
    init: (w, h, R) => ({
      dust: Array.from({ length: 24 }, () => ({ x: R(), y: R(), o: 0.1 + R() * 0.25 })),
    }),
    draw: (ctx, s, p, w, h, c, k) => {
      const cx = w / 2, cy = h / 2
      const doorT = ease(clamp(p / 0.3))
      ctx.globalAlpha = 0.22 * doorT; ctx.strokeStyle = c; ctx.lineWidth = 2
      ctx.strokeRect(cx - 70, cy - 110, 140, 220); ctx.globalAlpha = 1
      for (const d of s.dust) glow(ctx, d.x * w, d.y * h, 2.5, c, d.o * doorT)
      const raps = [0.25, 0.34, 0.62, 0.71]
      for (const at of raps) {
        const t = clamp((p - at) / 0.22); if (t <= 0 || t >= 1) continue
        ctx.globalAlpha = (1 - t) * 0.8; ctx.strokeStyle = c; ctx.lineWidth = 2.5
        ctx.beginPath(); ctx.arc(cx, cy, 20 + t * 110, 0, TAU); ctx.stroke(); ctx.globalAlpha = 1
      }
      const pulse = raps.some((at) => p > at && p < at + 0.1) ? 1 : 0.5
      glow(ctx, cx, cy, 34, c, 0.5 * doorT * pulse)
    },
  },
}

interface Props {
  vibeKey: string
  color: string
  duration?: number   // ms
  intensity?: number  // 0.68 soft · 1.0 warm · 1.45 charged
  fpsCap?: number     // throttle for small previews
}

export default function Signature({ vibeKey, color, duration = 1400, intensity = 1.0, fpsCap = 0 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = canvas.clientWidth, h = canvas.clientHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    ctx.scale(dpr, dpr)

    const sig = SIGNATURES[vibeKey] || SIGNATURES.LOVE
    const state = sig.init(w, h, Math.random)
    const start = performance.now()
    let raf = 0

    let lastDraw = 0
    const minGap = fpsCap > 0 ? 1000 / fpsCap : 0
    const frame = (now: number) => {
      if (!minGap || now - lastDraw >= minGap) {
        lastDraw = now
        const p = clamp((now - start) / duration)
        ctx.clearRect(0, 0, w, h)
        ctx.globalCompositeOperation = 'lighter'
        sig.draw(ctx, state, p, w, h, color, intensity)
        ctx.globalCompositeOperation = 'source-over'
        if (p >= 1) return
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [vibeKey, color, duration, intensity, fpsCap])

  return (
    <canvas
      ref={ref}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  )
}
